import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/litigationAgreement.css';

const hasNumericValue = (value) => value !== null && value !== '' && !Number.isNaN(Number(value));

const LancarAcordoLitigio = () => {
    const [documento, setDocumento] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [parcelas, setParcelas] = useState('');
    const [parcelaAtual, setParcelaAtual] = useState(1);
    const [valorParcela, setValorParcela] = useState(null);
    const [percentualLitigio, setPercentualLitigio] = useState(0);
    const [tipo, setTipo] = useState('TED');
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitLancarAcordo(e) {
        e.preventDefault();
        setIsSubmit(true);
        let vencimento = '';
        if (dateTime) {
            setError(false);
            vencimento = formatIsoDate(dateTime);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            documento,
            vencimento,
            parcelas,
            parcelaAtual,
            valorParcela,
            percentualLitigio,
            tipo
        });

        try {
            const response = await fetchFlask('lancarAcordoLitigio', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setDocumento('');
                setDateTime(null);
                setParcelas('');
                setTipo('TED');
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const paymentDateLabel = formatDateLabelPtBr(dateTime);
    const installmentValueLabel = hasNumericValue(valorParcela)
        ? formatCurrency(Number(valorParcela))
        : 'Não informado';
    const completedFields = [
        documento,
        dateTime,
        parcelas,
        parcelaAtual,
        hasNumericValue(valorParcela),
        hasNumericValue(percentualLitigio)
    ].filter(Boolean).length;

    return (
        <Box className='mainBox litigation-agreement-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Lançar Acordo Litígio
            </PageHeader>

            <form action='' method='POST' id='formAcordoLitigio' onSubmit={submitLancarAcordo} className='litigation-agreement-layout'>
                <Box className='litigation-agreement-workspace'>
                    <Box className='litigation-agreement-intro'>
                        <Box>
                            <h2>Recebimento de litígio com parâmetros auditáveis</h2>
                        </Box>
                        <p>
                            Informe o documento, confira a composição do acordo e defina o meio de pagamento antes de lançar.
                        </p>
                    </Box>

                    <Box className='litigation-agreement-panel'>
                        <Box className='litigation-agreement-section-heading'>
                            <GavelRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Identificação do Cliente</strong>
                            </Box>
                        </Box>

                        <Box className='litigation-agreement-document-strip'>
                            <BadgeRoundedIcon />
                            <TextField
                                required
                                label='CPF/CNPJ'
                                value={documento}
                                inputProps={{ maxLength: 18 }}
                                onChange={(e) => setDocumento(maskCpfCnpj(e.target.value))}
                            />
                        </Box>
                    </Box>

                    <Box className='litigation-agreement-panel'>
                        <Box className='litigation-agreement-section-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Composição do acordo</strong>
                            </Box>
                        </Box>

                        <Box className='litigation-agreement-fields-grid'>
                            <TextField
                                required
                                label='Número de parcelas'
                                type='number'
                                inputProps={{ step: 1, min: 1 }}
                                value={parcelas}
                                onChange={(e) => setParcelas(e.target.value)}
                            />
                            <TextField
                                required
                                label='Parcela atual'
                                type='number'
                                inputProps={{ step: 1, min: 1 }}
                                value={parcelaAtual}
                                onChange={(e) => setParcelaAtual(e.target.value)}
                            />
                            <TextField
                                required
                                label='Valor da parcela'
                                type='number'
                                inputProps={{ step: 0.01, min: 0 }}
                                value={valorParcela}
                                onChange={(e) => setValorParcela(e.target.value)}
                            />
                            <TextField
                                required
                                label='Percentual litígio (%)'
                                type='number'
                                inputProps={{ step: 0.01, min: 5 }}
                                value={percentualLitigio}
                                onChange={(e) => setPercentualLitigio(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='litigation-agreement-panel'>
                        <Box className='litigation-agreement-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Pagamento</strong>
                            </Box>
                        </Box>

                        <Box className='litigation-agreement-payment-grid'>
                            <FormControl className='litigation-agreement-date-control' error={error}>
                                <DayDatePicker
                                    id='data'
                                    label='Pagamento'
                                    minDate={new Date('2024-10-01')}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>

                            <Box className='litigation-agreement-payment-mode'>
                                <Box>
                                    <strong>{tipo === 'TED' ? 'TED (Bradesco)' : 'PIX (Omie Cash)'}</strong>
                                    <span>Meio de recebimento do acordo.</span>
                                </Box>
                                <FormControlLabel
                                    label={tipo}
                                    control={
                                        <Switch
                                            checked={tipo === 'PIX'}
                                            onChange={(e) => {
                                                setTipo(e.target.checked ? 'PIX' : 'TED');
                                            }}
                                        />
                                    }
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box className='litigation-agreement-summary'>
                    <Box className='litigation-agreement-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do lançamento</strong>
                        </Box>
                    </Box>

                    <Box className='litigation-agreement-summary-list'>
                        <Box>
                            <span>Documento</span>
                            <strong className={!documento ? 'is-empty' : undefined}>{documento || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Pagamento</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{paymentDateLabel}</strong>
                        </Box>
                        <Box>
                            <span>Parcelas</span>
                            <strong>{parcelaAtual || 0} de {parcelas || 0}</strong>
                        </Box>
                        <Box>
                            <span>Valor da parcela</span>
                            <strong>{installmentValueLabel}</strong>
                        </Box>
                        <Box>
                            <span>Percentual</span>
                            <strong>{hasNumericValue(percentualLitigio) ? `${percentualLitigio}%` : 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Tipo</span>
                            <strong>{tipo}</strong>
                        </Box>
                    </Box>

                    <Box className='litigation-agreement-attention'>
                        <AccountBalanceRoundedIcon />
                        <p>
                            O lançamento será enviado com o documento, vencimento, composição das parcelas e tipo de recebimento informados.
                        </p>
                    </Box>

                    <Box className='litigation-agreement-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/6 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<PaymentsRoundedIcon />}>
                        Lançar acordo
                    </SubmitButton>
                </Box>
            </form >
        </Box >
    );
};

export default LancarAcordoLitigio;
